import React, { useEffect } from 'react';
import logger from '../utils/logger';

interface InputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  label?: string;
  error?: string;
  helperText?: string;
}

const Input: React.FC<InputProps> = ({
  label,
  error,
  helperText,
  className = '',
  onChange,
  onFocus,
  onBlur,
  name,
  ...props
}) => {
  useEffect(() => {
    logger.debug('Input', 'Input rendered', { name, label, hasError: !!error, hasHelperText: !!helperText });
  }, [name, label, error, helperText]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    logger.debug('Input', 'Input value changed', { name, value: e.target.value, valueLength: e.target.value.length });
    if (onChange) {
      onChange(e);
    }
  };

  const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
    logger.debug('Input', 'Input focused', { name });
    if (onFocus) {
      onFocus(e);
    }
  };

  const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    logger.debug('Input', 'Input blurred', { name, value: e.target.value });
    if (onBlur) {
      onBlur(e);
    }
  };

  return (
    <div className="w-full">
      {label && (
        <label className="block text-sm font-medium text-text-secondary mb-2 font-elegant tracking-wide uppercase text-xs opacity-80">
          {label}
        </label>
      )}
      <input
        className={`
          w-full px-4 py-3.5 rounded-2xl
          backdrop-blur-xl transition-all duration-300
          font-elegant text-base tracking-wide
          border
          placeholder-white/20
          ${error
            ? 'bg-red-500/10 border-red-500/30 text-red-200 focus:border-red-500/50'
            : 'bg-white/5 border-white/10 text-white/90 hover:bg-white/10 hover:border-white/20 focus:bg-white/10 focus:border-white/30 focus:shadow-[0_0_20px_rgba(255,255,255,0.1)]'
          }
          focus:outline-none focus:ring-0
          ${className}
        `}
        onChange={handleChange}
        onFocus={handleFocus}
        onBlur={handleBlur}
        name={name}
        {...props}
      />
      {error && (
        <p className="mt-1.5 text-xs text-red-400 font-elegant tracking-wide ml-1">{error}</p>
      )}
      {helperText && !error && (
        <p className="mt-1.5 text-xs text-text-tertiary font-elegant tracking-wide ml-1 opacity-60">{helperText}</p>
      )}
    </div>
  );
};

export default Input;

